import { ReactElement, SyntheticEvent, useState } from 'react';

import { UploadResult } from 'firebase/storage';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import { useAuth } from '../../contextProviders/AuthProvider';
import League from '../../types/League';
import { CompetitiveLevels } from '../../utils/Compete';
import GratiColorPicker from '../common/GratiColorPicker';
import GratiImageUpload from '../common/GratiImageUpload';
import OrgSeasonMonthDaysEditor from '../org/OrgSeasonMonthDaysEditor';
import OrgSelector from '../org/OrgSelector';

export interface LeagueGeneralSettingsEditorProps {
  league: League;
  isNew?: boolean;
  onUpdate: <K extends keyof League>(field: K, value: League[K]) => void;
  onOrgChange?: (orgPair: { orgId: string; orgName: string }) => void;
  onImageUpdated?: (snapshot: UploadResult | null) => void;
  onFileSelected?: (file: File | null) => void;
}

export default function LeagueGeneralSettingsEditor(props: LeagueGeneralSettingsEditorProps): ReactElement {
  const { league, isNew, onUpdate, onOrgChange, onImageUpdated, onFileSelected } = props;
  const { isGod } = useAuth();

  const [competitiveHover, setCompetitiveHover] = useState(-1);

  return (
    <Stack spacing={2}>
      {isNew && onOrgChange && (
        <Box sx={{ maxWidth: 'sm' }}>
          <OrgSelector
            value={league.orgId}
            onChange={onOrgChange}
          />
        </Box>
      )}
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <TextField
          id="name"
          label="League Name"
          aria-label="League Name"
          aria-required="true"
          required
          autoComplete="off"
          slotProps={{ htmlInput: { 'data-1p-ignore': true } }}
          value={league.name}
          onChange={({ target }) => onUpdate('name', target.value)}
          sx={{ flexGrow: 1, maxWidth: 'sm' }}
        />
        <GratiImageUpload
          onImageUpdated={isNew ? undefined : onImageUpdated}
          onFileSelected={isNew ? onFileSelected : undefined}
          deferUpload={isNew}
          isInitialized={!isNew && Boolean(league.logo)}
          storageImageFileName={league.itemId + '-logo'}
          storageImageLocation={`org/${league.orgId}/league/${league.itemId}/orgMemberLeagueAdminFile`}
          label="Logo"
          isCompact
          size="large"
        />
      </Stack>
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <TextField
          id="description"
          label="Description"
          aria-label="Description"
          aria-required="false"
          multiline
          minRows={2}
          value={league.description}
          onChange={({ target }) => onUpdate('description', target.value)}
          sx={{ flexGrow: 1, maxWidth: 'sm' }}
        />
        <GratiColorPicker
          value={league.color}
          onChange={(color) => onUpdate('color', color)}
        />
      </Stack>
      <Stack direction="row">
        <Typography variant="bodyLarge" sx={{ pr: 2 }}>
          Competition{' '}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Rating
            id="competitionLevel"
            name="hover-feedback"
            aria-label="Competitive Level"
            value={league.competitionLevel}
            precision={1}
            icon={<EmojiEventsIcon fontSize="inherit" />}
            getLabelText={(value: number) => `$Competitive Level ${value}`}
            onChange={(_event: SyntheticEvent, newValue: number | null) => {
              onUpdate('competitionLevel', newValue ? newValue : 0);
            }}
            onChangeActive={(_event: SyntheticEvent, hover: number) => {
              setCompetitiveHover(hover);
            }}
            emptyIcon={<EmojiEventsIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
          />
          {league.competitionLevel !== null && (
            <Box sx={{ ml: '1rem' }}>
              {CompetitiveLevels[competitiveHover !== -1 ? competitiveHover : league.competitionLevel]}
            </Box>
          )}
        </Box>
      </Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={league.includeAllOrgMembers}
            onChange={(e) => {
              onUpdate('includeAllOrgMembers', e.target.checked);
            }}
          />
        }
        label="Automatically add new org members to this league"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={league.isTeam}
            onChange={(e) => {
              onUpdate('isTeam', e.target.checked);
            }}
          />
        }
        label="This is a team league (members can be organized into teams)"
      />
      {isGod && (
        <FormControlLabel
          control={
            <Checkbox
              checked={league.areEventsGroupedByDivision ?? false}
              onChange={(e) => {
                onUpdate('areEventsGroupedByDivision', e.target.checked);
              }}
            />
          }
          label="Group event attendees and tee sheet by division (default for new events)"
        />
      )}
      <OrgSeasonMonthDaysEditor
        label="Default season dates"
        value={league.seasonMonthDays ?? []}
        onChange={(seasonMonthDays) => onUpdate('seasonMonthDays', seasonMonthDays)}
      />
    </Stack>
  );
}
